import _ from 'lodash';
import React, { useState, useEffect, useMemo } from 'react';
import { PropsValue } from 'react-select';
import { format } from 'date-fns';
import { AsyncSelect, BaseCheckbox, BaseSelect, DatePicker, ISelectOption } from '../../../base';
import { FilterMethod, IColumnFilterCustomProps } from '../filters/filters.models';
import { getFilterMethods, isEmptyValueMethod } from '../filters/filters.service';
import * as S from './author-date-filter.styles';

interface IAuthorDateFilterValue {
    author?: { method: FilterMethod; value: string };
    date?: { method: FilterMethod; value: Date };
}

interface IAuthorDateFilterProps extends IColumnFilterCustomProps {
    value: IAuthorDateFilterValue;
    onChange: (value: IAuthorDateFilterValue) => void;
    filterMethod?: FilterMethod;
    onFilterMethodChange?: (method: FilterMethod) => void;
    loadOptions?: (searchTerm: string) => Promise<ISelectOption[]>;
    /**
     * Optional current user ID to support "Self" checkbox (same pattern as AssigneeFilter).
     */
    currentUserId?: string;
    /**
     * Label for the "Self" checkbox (default: "Self").
     */
    currentUserLabel?: string;
}

export const AuthorDateFilter: React.FC<IAuthorDateFilterProps> = ({
    value,
    onChange,
    loadOptions,
    currentUserId,
    currentUserLabel = 'Self',
}) => {
    const [authorMethod, setAuthorMethod] = useState<FilterMethod>(
        value?.author?.method || FilterMethod.LK
    );
    const [authorValue, setAuthorValue] = useState<string>(value?.author?.value || '');
    const [dateMethod, setDateMethod] = useState<FilterMethod>(
        value?.date?.method || FilterMethod.EQ
    );
    const [dateValue, setDateValue] = useState<Date | undefined>(value?.date?.value);

    const [authorOptions, setAuthorOptions] = useState<ISelectOption[]>([]);
    const selectedAuthorOption = useMemo(() => {
        if (!authorValue) {
            return null;
        }
        const found = authorOptions.find(opt => String(opt.value) === String(authorValue));
        if (found) {
            return found;
        }
        // For "Self" show nice label when we know currentUserId
        if (currentUserId && authorValue === currentUserId) {
            return { label: currentUserLabel, value: currentUserId };
        }
        // Fallback: show raw ID if we don't have a matching option yet
        return { label: authorValue, value: authorValue };
    }, [authorValue, authorOptions, currentUserId, currentUserLabel]);

    // Sync state when value prop changes
    useEffect(() => {
        if (value?.author) {
            setAuthorMethod(value.author.method);
            setAuthorValue(value.author.value);
        }
        if (value?.date) {
            setDateMethod(value.date.method);
            setDateValue(value.date.value);
        }
    }, [value]);

    const authorMethods = getFilterMethods('String');
    const dateMethods = getFilterMethods('DateTime');

    const authorEmptyMethod = isEmptyValueMethod(authorMethod);
    const dateEmptyMethod = isEmptyValueMethod(dateMethod);

    const selectedAuthorMethod = _.find(authorMethods, { value: authorMethod });
    const selectedDateMethod = _.find(dateMethods, { value: dateMethod });

    const handleAuthorMethodChange = (option: PropsValue<ISelectOption>) => {
        if (!option) {
            return;
        }
        const selectedOption = option as ISelectOption;
        const newMethod = selectedOption.value as FilterMethod;
        setAuthorMethod(newMethod);
        
        // Update the combined value
        const newValue: IAuthorDateFilterValue = {
            author: { method: newMethod, value: authorValue },
            date: dateValue ? { method: dateMethod, value: dateValue } : undefined,
        };
        onChange(newValue);
    };

    const handleAuthorSelectChange = (selected: any) => {
        const option = Array.isArray(selected) ? selected[0] : selected;
        const id = option ? String((option as ISelectOption).value || '') : '';
        setAuthorValue(id);

        const combinedValue: IAuthorDateFilterValue = {
            author: id ? { method: authorMethod, value: id } : undefined,
            date: dateValue ? { method: dateMethod, value: dateValue } : undefined,
        };
        onChange(combinedValue);
    };

    const handleDateMethodChange = (option: PropsValue<ISelectOption>) => {
        if (!option) {
            return;
        }
        const selectedOption = option as ISelectOption;
        const newMethod = selectedOption.value as FilterMethod;
        setDateMethod(newMethod);
        
        // Update the combined value
        const newValue: IAuthorDateFilterValue = {
            author: authorValue ? { method: authorMethod, value: authorValue } : undefined,
            date: { method: newMethod, value: dateValue },
        };
        onChange(newValue);
    };

    const handleDateValueChange = (date: Date | undefined) => {
        // Keep the date in local time (same behavior as generic Date/DateTime filters)
        // to avoid off-by-one issues when the backend interprets the value.
        setDateValue(date);

        const newValue: IAuthorDateFilterValue = {
            author: authorValue ? { method: authorMethod, value: authorValue } : undefined,
            date: date ? { method: dateMethod, value: date } : undefined,
        };
        onChange(newValue);
    };

    const handleClearAuthor = () => {
        setAuthorValue('');
        setAuthorMethod(FilterMethod.LK);
        
        // Update the combined value without author
        const newValue: IAuthorDateFilterValue = {
            author: undefined,
            date: dateValue ? { method: dateMethod, value: dateValue } : undefined,
        };
        onChange(newValue);
    };

    const handleClearDate = () => {
        setDateValue(undefined);
        setDateMethod(FilterMethod.EQ);
        
        // Update the combined value without date
        const newValue: IAuthorDateFilterValue = {
            author: authorValue ? { method: authorMethod, value: authorValue } : undefined,
            date: undefined,
        };
        onChange(newValue);
    };

    useEffect(() => {
        // Preload options once for display mapping (same idea as AssigneeFilter)
        if (!loadOptions) {
            return;
        }

        let isMounted = true;
        loadOptions('')
            .then(options => {
                if (isMounted) {
                    setAuthorOptions(options || []);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setAuthorOptions([]);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [loadOptions]);

    const loadAuthorOptions = async (searchTerm: string): Promise<ISelectOption[]> => {
        if (!loadOptions) {
            return [];
        }
        return loadOptions(searchTerm);
    };

    const isSelfSelected = useMemo(() => {
        if (!currentUserId) {
            return false;
        }
        return authorValue === currentUserId;
    }, [authorValue, currentUserId]);

    const handleSelfToggle = (checked: boolean) => {
        if (!currentUserId) {
            return;
        }

        if (checked) {
            setAuthorValue(currentUserId);
            const newValue: IAuthorDateFilterValue = {
                author: { method: authorMethod, value: currentUserId },
                date: dateValue ? { method: dateMethod, value: dateValue } : undefined,
            };
            onChange(newValue);
        } else if (authorValue === currentUserId) {
            setAuthorValue('');
            const newValue: IAuthorDateFilterValue = {
                author: undefined,
                date: dateValue ? { method: dateMethod, value: dateValue } : undefined,
            };
            onChange(newValue);
        }
    };

    return (
        <S.FilterContainer>
            <S.FilterSection>
                <S.SectionHeader>
                    <S.SectionLabel>Author</S.SectionLabel>
                    <S.ClearButton onClick={handleClearAuthor}>Clear</S.ClearButton>
                </S.SectionHeader>
                <S.FilterRow>
                    <BaseSelect
                        menuPosition="fixed"
                        onChange={handleAuthorMethodChange}
                        options={authorMethods}
                        value={selectedAuthorMethod}
                        placeholder="Condition"
                    />
                </S.FilterRow>
                {!authorEmptyMethod && (
                    <>
                        <S.FilterRow>
                            <AsyncSelect
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                defaultOptions={true}
                                cacheOptions={true}
                                loadOptions={loadAuthorOptions}
                                placeholder="Search users..."
                                value={selectedAuthorOption}
                                onChange={handleAuthorSelectChange}
                                isMulti={false}
                            />
                        </S.FilterRow>
                        {currentUserId && (
                            <S.FilterRow>
                                <BaseCheckbox
                                    checked={isSelfSelected}
                                    onChange={e => handleSelfToggle(e.target.checked)}
                                    label={currentUserLabel}
                                />
                            </S.FilterRow>
                        )}
                    </>
                )}
            </S.FilterSection>

            <S.Divider />

            <S.FilterSection>
                <S.SectionHeader>
                    <S.SectionLabel>Date</S.SectionLabel>
                    <S.ClearButton onClick={handleClearDate}>Clear</S.ClearButton>
                </S.SectionHeader>
                <S.FilterRow>
                    <BaseSelect
                        menuPosition="fixed"
                        onChange={handleDateMethodChange}
                        options={dateMethods}
                        value={selectedDateMethod}
                        placeholder="Condition"
                    />
                </S.FilterRow>
                {!dateEmptyMethod && (
                    <S.FilterRow>
                        <DatePicker
                            value={dateValue ? format(dateValue, 'yyyy-MM-dd') : undefined}
                            onDateSelected={handleDateValueChange}
                            format="yyyy-MM-dd"
                        />
                    </S.FilterRow>
                )}
            </S.FilterSection>
        </S.FilterContainer>
    );
};

